import { useEffect, useMemo } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query';
import {
  projectAnnotationApi,
  type ProjectAnnotationListParams,
  type ProjectAnnotationTargetCategory,
} from '@/services/api/project-annotations';
import type {
  ProjectAnnotationContext,
  ProjectAnnotation,
  ProjectAnnotationAcceptancePayload,
  ProjectAnnotationCreatePayload,
  ProjectAnnotationListResponse,
  ProjectAnnotationReplyCreatePayload,
  ProjectAnnotationStatus,
} from '@/types/project-annotations';
import {
  matchesAnnotationTargetCategoryFilter,
  type AnnotationTargetCategoryFilter,
} from '@/lib/annotations/annotation-filters';
import {
  buildActionableResourceLibraryCounts,
  buildActionableStoryboardShotCounts,
  buildActionableStoryboardTrackCounts,
  buildActionableTargetCounts,
  isAnnotationActionable,
} from '@/lib/annotations/annotation-actionable';
import { useAppStore } from '@/stores/appStore';

const PROJECT_ANNOTATION_PAGE_SIZE = 100;

export interface ProjectAnnotationActionableCount {
  received: number;
  sent: number;
  total: number;
}

export type ProjectAnnotationListScope =
  | 'panel'
  | 'target'
  | 'preview-target'
  | 'parse-flow-aggregate'
  | 'actionable-index';

const PROJECT_ANNOTATION_LIST_SCOPES: readonly ProjectAnnotationListScope[] = [
  'panel',
  'target',
  'preview-target',
  'parse-flow-aggregate',
  'actionable-index',
];

type ProjectAnnotationRefetchType = 'active' | 'inactive' | 'all' | 'none';

interface ProjectAnnotationListOptions {
  enabled?: boolean;
  filters?: ProjectAnnotationListParams;
  scope?: ProjectAnnotationListScope;
}

const projectAnnotationKeys = {
  all: ['project-annotations'] as const,
  listRoot: (projectId: string, screenplayId: string) =>
    [...projectAnnotationKeys.all, 'list', projectId, screenplayId] as const,
  listScope: (
    projectId: string,
    screenplayId: string,
    scope: ProjectAnnotationListScope = 'panel'
  ) => [...projectAnnotationKeys.listRoot(projectId, screenplayId), scope] as const,
  list: (projectId: string, screenplayId: string, filters?: ProjectAnnotationListParams) =>
    [
      ...projectAnnotationKeys.listScope(projectId, screenplayId),
      normalizeProjectAnnotationFilters(filters),
    ] as const,
  infiniteList: (
    projectId: string,
    screenplayId: string,
    scope: ProjectAnnotationListScope,
    filters?: ProjectAnnotationListParams
  ) =>
    [
      ...projectAnnotationKeys.listScope(projectId, screenplayId, scope),
      'infinite',
      normalizeProjectAnnotationFilters(filters),
    ] as const,
  unreadCount: (
    projectId: string,
    screenplayId: string,
    targetCategory?: ProjectAnnotationTargetCategory
  ) =>
    [
      ...projectAnnotationKeys.all,
      'unread-count',
      projectId,
      screenplayId,
      targetCategory ?? 'all',
    ] as const,
  actionableCount: (
    projectId: string,
    screenplayId: string,
    context?: ProjectAnnotationContext,
    targetCategory?: ProjectAnnotationTargetCategory,
    targetStoryboardId?: string | null
  ) =>
    [
      ...projectAnnotationKeys.all,
      'actionable-count',
      projectId,
      screenplayId,
      context ?? 'all',
      targetCategory ?? 'all',
      targetStoryboardId ?? 'all',
    ] as const,
};

export function normalizeProjectAnnotationFilters(
  filters?: ProjectAnnotationListParams
): ProjectAnnotationListParams {
  return {
    ...(filters?.page !== undefined ? { page: filters.page } : {}),
    ...(filters?.pageSize !== undefined ? { pageSize: filters.pageSize } : {}),
    ...(filters?.status !== undefined ? { status: filters.status } : {}),
    ...(filters?.statuses !== undefined ? { statuses: filters.statuses } : {}),
    ...(filters?.view ? { view: filters.view } : {}),
    ...(filters?.context ? { context: filters.context } : {}),
    ...(filters?.targetType ? { targetType: filters.targetType } : {}),
    ...(filters?.targetCategory ? { targetCategory: filters.targetCategory } : {}),
    ...(filters?.targetId ? { targetId: filters.targetId } : {}),
    ...(filters?.targetStoryboardId ? { targetStoryboardId: filters.targetStoryboardId } : {}),
    ...(filters?.includeClosed !== undefined ? { includeClosed: filters.includeClosed } : {}),
  };
}

export function buildProjectAnnotationListQueryKey(
  projectId: string,
  screenplayId: string,
  options?: { filters?: ProjectAnnotationListParams; scope?: ProjectAnnotationListScope }
) {
  return projectAnnotationKeys.infiniteList(
    projectId,
    screenplayId,
    options?.scope ?? 'panel',
    normalizeProjectAnnotationFilters(options?.filters)
  );
}

function updateAnnotationInPage(
  page: ProjectAnnotationListResponse,
  annotationId: string,
  updater: (annotation: ProjectAnnotation) => ProjectAnnotation
): ProjectAnnotationListResponse {
  return {
    ...page,
    items: page.items.map((annotation) =>
      annotation.annotationId === annotationId ? updater(annotation) : annotation
    ),
  };
}

function pageHasUnreadAnnotation(
  page: ProjectAnnotationListResponse,
  annotationId: string
): boolean {
  return page.items.some(
    (annotation) => annotation.annotationId === annotationId && annotation.isUnread
  );
}

function findUnreadAnnotation(
  lists: Array<readonly [unknown, InfiniteData<ProjectAnnotationListResponse> | undefined]>,
  annotationId: string
): ProjectAnnotation | null {
  for (const [, list] of lists) {
    for (const page of list?.pages ?? []) {
      const annotation = page.items.find(
        (item) => item.annotationId === annotationId && item.isUnread
      );
      if (annotation) return annotation;
    }
  }

  return null;
}

function findAnnotationInLists(
  lists: Array<readonly [unknown, InfiniteData<ProjectAnnotationListResponse> | undefined]>,
  annotationId: string
): ProjectAnnotation | null {
  for (const [, list] of lists) {
    for (const page of list?.pages ?? []) {
      const annotation = page.items.find((item) => item.annotationId === annotationId);
      if (annotation) return annotation;
    }
  }

  return null;
}

function resolveCurrentUserId(): string | null {
  return useAppStore.getState().user?.userId ?? null;
}

function withRecomputedActionable(
  annotation: ProjectAnnotation,
  status: ProjectAnnotationStatus,
  currentUserId: string | null
): ProjectAnnotation {
  return {
    ...annotation,
    status,
    isActionable: isAnnotationActionable({ ...annotation, status }, currentUserId),
  };
}

function replaceAnnotationInListCaches(
  queryClient: QueryClient,
  projectId: string,
  screenplayId: string,
  updatedAnnotation: ProjectAnnotation
): void {
  queryClient.setQueriesData<InfiniteData<ProjectAnnotationListResponse>>(
    { queryKey: projectAnnotationKeys.listRoot(projectId, screenplayId), exact: false },
    (current) =>
      current
        ? {
            ...current,
            pages: current.pages.map((page) =>
              updateAnnotationInPage(page, updatedAnnotation.annotationId, () => updatedAnnotation)
            ),
          }
        : current
  );
}

/**
 * 局部列表（target）靠原地写回立刻刷新；全局 panel 按状态服务端筛选，
 * 必须失效列表否则「待反馈 → 已完成」后旧筛选项仍停在缓存里。
 */
export function syncProjectAnnotationCachesAfterMutation(
  queryClient: QueryClient,
  projectId: string,
  screenplayId: string,
  updatedAnnotation: ProjectAnnotation
): void {
  replaceAnnotationInListCaches(queryClient, projectId, screenplayId, updatedAnnotation);
  invalidateProjectAnnotationQueries(queryClient, projectId, screenplayId);
}

export function invalidateProjectAnnotationCounts(
  queryClient: QueryClient,
  projectId: string,
  screenplayId: string,
  refetchType: ProjectAnnotationRefetchType = 'active'
): void {
  void queryClient.invalidateQueries({
    queryKey: [...projectAnnotationKeys.all, 'unread-count', projectId, screenplayId],
    exact: false,
    refetchType,
  });
  void queryClient.invalidateQueries({
    queryKey: [...projectAnnotationKeys.all, 'actionable-count', projectId, screenplayId],
    exact: false,
    refetchType,
  });
}

export function invalidateProjectAnnotationQueries(
  queryClient: QueryClient,
  projectId: string,
  screenplayId: string
): void {
  invalidateProjectAnnotationList(queryClient, projectId, screenplayId, {
    scopes: PROJECT_ANNOTATION_LIST_SCOPES,
  });
  invalidateProjectAnnotationCounts(queryClient, projectId, screenplayId);
}

/**
 * 仅失效批注列表（不触发未读数/待办数请求）。
 * 用于回复成功等「未读数已由乐观更新维护、只需刷新列表内容」的场景，避免冗余计数请求。
 */
export function invalidateProjectAnnotationList(
  queryClient: QueryClient,
  projectId: string,
  screenplayId: string,
  options?: {
    scopes?: readonly ProjectAnnotationListScope[];
    refetchType?: ProjectAnnotationRefetchType;
  }
): void {
  const scopes = options?.scopes ?? (['panel', 'target'] as const);
  for (const scope of scopes) {
    void queryClient.invalidateQueries({
      queryKey: projectAnnotationKeys.listScope(projectId, screenplayId, scope),
      exact: false,
      refetchType: options?.refetchType ?? 'active',
    });
  }
}

export function useProjectAnnotations(
  projectId: string | null,
  screenplayId: string | null,
  options?: ProjectAnnotationListOptions
) {
  const filters = normalizeProjectAnnotationFilters({
    ...options?.filters,
    pageSize: options?.filters?.pageSize ?? PROJECT_ANNOTATION_PAGE_SIZE,
  });
  const scope = options?.scope ?? 'panel';
  return useInfiniteQuery<ProjectAnnotationListResponse>({
    queryKey: projectAnnotationKeys.infiniteList(
      projectId ?? '',
      screenplayId ?? '',
      scope,
      filters
    ),
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      projectAnnotationApi.list(projectId!, screenplayId!, {
        ...filters,
        page: Number(pageParam),
      }),
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
    enabled: Boolean(projectId && screenplayId) && (options?.enabled ?? true),
    staleTime: 15_000,
  });
}

export function useProjectAnnotationActionableIndex(
  projectId: string | null,
  screenplayId: string | null,
  options?: { enabled?: boolean; context?: ProjectAnnotationContext }
) {
  const currentUserId = useAppStore((state) => state.user?.userId ?? null);
  const annotationsQuery = useProjectAnnotations(projectId, screenplayId, {
    enabled: Boolean(projectId && screenplayId && currentUserId) && (options?.enabled ?? true),
    scope: 'actionable-index',
    filters: {
      ...(options?.context ? { context: options.context } : {}),
      includeClosed: true,
      pageSize: PROJECT_ANNOTATION_PAGE_SIZE,
    },
  });

  useEffect(() => {
    if (annotationsQuery.hasNextPage && !annotationsQuery.isFetchingNextPage) {
      void annotationsQuery.fetchNextPage();
    }
  }, [
    annotationsQuery.fetchNextPage,
    annotationsQuery.hasNextPage,
    annotationsQuery.isFetchingNextPage,
  ]);

  return useMemo(() => {
    const annotations = annotationsQuery.data?.pages.flatMap((page) => page.items) ?? [];
    return {
      storyboardShotCounts: buildActionableStoryboardShotCounts(annotations, currentUserId),
      storyboardTrackCounts: buildActionableStoryboardTrackCounts(annotations, currentUserId),
      resourceLibraryCounts: buildActionableResourceLibraryCounts(annotations, currentUserId),
      targetCounts: buildActionableTargetCounts(annotations, currentUserId),
      isLoading: annotationsQuery.isLoading,
    };
  }, [annotationsQuery.data, annotationsQuery.isLoading, currentUserId]);
}

export function useProjectAnnotationUnreadCount(
  projectId: string | null,
  screenplayId: string | null,
  options?: { targetCategory?: ProjectAnnotationTargetCategory }
) {
  const targetCategory = options?.targetCategory;
  return useQuery({
    queryKey: projectAnnotationKeys.unreadCount(
      projectId ?? '',
      screenplayId ?? '',
      targetCategory
    ),
    queryFn: () =>
      projectAnnotationApi.unreadCount(projectId!, screenplayId!, {
        ...(targetCategory ? { targetCategory } : {}),
      }),
    enabled: Boolean(projectId && screenplayId),
    staleTime: 15_000,
  });
}

export function useProjectAnnotationActionableCount(
  projectId: string | null,
  screenplayId: string | null,
  options?: {
    context?: ProjectAnnotationContext;
    targetCategory?: ProjectAnnotationTargetCategory;
    targetStoryboardId?: string | null;
    enabled?: boolean;
  }
) {
  const context = options?.context;
  const targetCategory = options?.targetCategory;
  const targetStoryboardId = options?.targetStoryboardId;
  return useQuery({
    queryKey: projectAnnotationKeys.actionableCount(
      projectId ?? '',
      screenplayId ?? '',
      context,
      targetCategory,
      targetStoryboardId
    ),
    queryFn: () =>
      projectAnnotationApi.actionableCount(projectId!, screenplayId!, {
        ...(context ? { context } : {}),
        ...(targetCategory ? { targetCategory } : {}),
        ...(targetStoryboardId ? { targetStoryboardId } : {}),
      }),
    enabled: Boolean(projectId && screenplayId) && (options?.enabled ?? true),
    staleTime: 15_000,
  });
}

export function useCreateProjectAnnotation(projectId: string | null, screenplayId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ProjectAnnotationCreatePayload) =>
      projectAnnotationApi.create(projectId!, screenplayId!, payload),
    onSuccess: () => {
      if (!projectId || !screenplayId) return;
      invalidateProjectAnnotationQueries(queryClient, projectId, screenplayId);
    },
  });
}

function decrementUnreadCountCaches(
  queryClient: QueryClient,
  projectId: string,
  screenplayId: string,
  unreadAnnotation: ProjectAnnotation | null
): void {
  const unreadCountQueries = queryClient.getQueriesData<{ count: number }>({
    queryKey: [...projectAnnotationKeys.all, 'unread-count', projectId, screenplayId],
    exact: false,
  });

  for (const [queryKey, current] of unreadCountQueries) {
    if (!current) continue;

    const targetCategory = queryKey[4] as AnnotationTargetCategoryFilter | 'all' | undefined;
    if (
      targetCategory &&
      targetCategory !== 'all' &&
      unreadAnnotation &&
      !matchesAnnotationTargetCategoryFilter(unreadAnnotation, targetCategory)
    ) {
      continue;
    }

    queryClient.setQueryData(queryKey, { count: Math.max(0, current.count - 1) });
  }
}

function matchesActionableCountCache(
  queryKey: readonly unknown[],
  annotation: ProjectAnnotation
): boolean {
  const context = queryKey[4] as ProjectAnnotationContext | 'all' | undefined;
  if (context === 'project_page' && annotation.context !== 1) return false;
  if (context === 'parse_flow' && annotation.context !== 2) return false;

  const targetCategory = queryKey[5] as AnnotationTargetCategoryFilter | 'all' | undefined;
  if (
    targetCategory &&
    targetCategory !== 'all' &&
    !matchesAnnotationTargetCategoryFilter(annotation, targetCategory)
  ) {
    return false;
  }

  const targetStoryboardId = queryKey[6] as string | 'all' | undefined;
  if (
    targetStoryboardId &&
    targetStoryboardId !== 'all' &&
    annotation.targetStoryboardId !== targetStoryboardId &&
    !(annotation.targetType === 'storyboard' && annotation.targetId === targetStoryboardId)
  ) {
    return false;
  }

  return true;
}

function decrementUnreadReplyActionableCountCaches(
  queryClient: QueryClient,
  projectId: string,
  screenplayId: string,
  annotation: ProjectAnnotation,
  currentUserId: string
): void {
  const actionableCountQueries = queryClient.getQueriesData<ProjectAnnotationActionableCount>({
    queryKey: [...projectAnnotationKeys.all, 'actionable-count', projectId, screenplayId],
    exact: false,
  });
  const targetBucket = annotation.authorUserId === currentUserId ? 'sent' : 'received';

  for (const [queryKey, current] of actionableCountQueries) {
    if (!current || !matchesActionableCountCache(queryKey as readonly unknown[], annotation)) {
      continue;
    }

    queryClient.setQueryData(queryKey, {
      received: targetBucket === 'received' ? Math.max(0, current.received - 1) : current.received,
      sent: targetBucket === 'sent' ? Math.max(0, current.sent - 1) : current.sent,
      total: Math.max(0, current.total - 1),
    });
  }
}

export function shouldDecrementUnreadReplyActionableCount(
  annotation: ProjectAnnotation | null,
  currentUserId: string | null
): boolean {
  return Boolean(
    annotation?.hasUnreadReply &&
    currentUserId &&
    !isAnnotationActionable(annotation, currentUserId)
  );
}

export function useMarkProjectAnnotationRead(
  projectId: string | null,
  screenplayId: string | null
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (annotationId: string) => projectAnnotationApi.markRead(annotationId),
    onMutate: async (annotationId) => {
      if (!projectId || !screenplayId) return undefined;
      const listBaseKey = projectAnnotationKeys.listRoot(projectId, screenplayId);
      const unreadCountBaseKey = [
        ...projectAnnotationKeys.all,
        'unread-count',
        projectId,
        screenplayId,
      ] as const;
      await Promise.all([
        queryClient.cancelQueries({ queryKey: listBaseKey, exact: false }),
        queryClient.cancelQueries({ queryKey: unreadCountBaseKey, exact: false }),
      ]);
      const previousLists = queryClient.getQueriesData<InfiniteData<ProjectAnnotationListResponse>>(
        {
          queryKey: listBaseKey,
          exact: false,
        }
      );
      const previousUnreadCounts = queryClient.getQueriesData<{ count: number }>({
        queryKey: unreadCountBaseKey,
        exact: false,
      });
      const unreadAnnotation = findUnreadAnnotation(previousLists, annotationId);
      const wasUnread = previousLists.some(([, list]) =>
        list?.pages.some((page) => pageHasUnreadAnnotation(page, annotationId))
      );

      queryClient.setQueriesData<InfiniteData<ProjectAnnotationListResponse>>(
        { queryKey: listBaseKey, exact: false },
        (current) =>
          current
            ? {
                ...current,
                pages: current.pages.map((page) =>
                  updateAnnotationInPage(page, annotationId, (annotation) => ({
                    ...annotation,
                    isUnread: false,
                  }))
                ),
              }
            : current
      );

      if (wasUnread) {
        decrementUnreadCountCaches(queryClient, projectId, screenplayId, unreadAnnotation);
      }

      return { previousLists, previousUnreadCounts };
    },
    onError: (_error, _annotationId, context) => {
      if (!projectId || !screenplayId || !context) return;
      for (const [key, data] of context.previousLists) {
        queryClient.setQueryData(key, data);
      }
      for (const [key, data] of context.previousUnreadCounts) {
        queryClient.setQueryData(key, data);
      }
    },
  });
}

export function useMarkProjectAnnotationReplyRead(
  projectId: string | null,
  screenplayId: string | null
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (annotationId: string) => projectAnnotationApi.markReplyRead(annotationId),
    onMutate: async (annotationId) => {
      if (!projectId || !screenplayId) return undefined;
      const listBaseKey = projectAnnotationKeys.listRoot(projectId, screenplayId);
      const actionableCountBaseKey = [
        ...projectAnnotationKeys.all,
        'actionable-count',
        projectId,
        screenplayId,
      ] as const;
      await Promise.all([
        queryClient.cancelQueries({ queryKey: listBaseKey, exact: false }),
        queryClient.cancelQueries({ queryKey: actionableCountBaseKey, exact: false }),
      ]);
      const previousLists = queryClient.getQueriesData<InfiniteData<ProjectAnnotationListResponse>>(
        {
          queryKey: listBaseKey,
          exact: false,
        }
      );
      const previousActionableCounts = queryClient.getQueriesData<ProjectAnnotationActionableCount>(
        {
          queryKey: actionableCountBaseKey,
          exact: false,
        }
      );
      const currentUserId = resolveCurrentUserId();
      const cachedAnnotation = findAnnotationInLists(previousLists, annotationId);
      const shouldDecrementActionableCount = shouldDecrementUnreadReplyActionableCount(
        cachedAnnotation,
        currentUserId
      );

      queryClient.setQueriesData<InfiniteData<ProjectAnnotationListResponse>>(
        { queryKey: listBaseKey, exact: false },
        (current) =>
          current
            ? {
                ...current,
                pages: current.pages.map((page) =>
                  updateAnnotationInPage(page, annotationId, (annotation) => ({
                    ...annotation,
                    hasUnreadReply: false,
                  }))
                ),
              }
            : current
      );

      if (shouldDecrementActionableCount && cachedAnnotation && currentUserId) {
        decrementUnreadReplyActionableCountCaches(
          queryClient,
          projectId,
          screenplayId,
          cachedAnnotation,
          currentUserId
        );
      }

      return { previousLists, previousActionableCounts };
    },
    onError: (_error, _annotationId, context) => {
      if (!projectId || !screenplayId || !context) return;
      for (const [key, data] of context.previousLists) {
        queryClient.setQueryData(key, data);
      }
      for (const [key, data] of context.previousActionableCounts) {
        queryClient.setQueryData(key, data);
      }
    },
  });
}

export function useCreateProjectAnnotationReply(
  projectId: string | null,
  screenplayId: string | null
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      annotationId,
      payload,
    }: {
      annotationId: string;
      payload: ProjectAnnotationReplyCreatePayload;
    }) => projectAnnotationApi.reply(annotationId, payload),
    onSuccess: () => {
      if (!projectId || !screenplayId) return;
      invalidateProjectAnnotationList(queryClient, projectId, screenplayId, {
        scopes: ['panel', 'target', 'preview-target'],
      });
    },
  });
}

export function useUpdateProjectAnnotationStatus(
  projectId: string | null,
  screenplayId: string | null
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      annotationId,
      status,
    }: {
      annotationId: string;
      status: ProjectAnnotationStatus;
    }) => projectAnnotationApi.updateStatus(annotationId, { status }),
    onMutate: async ({ annotationId, status }) => {
      if (!projectId || !screenplayId) return undefined;
      const currentUserId = resolveCurrentUserId();
      const queryKey = projectAnnotationKeys.listRoot(projectId, screenplayId);
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueriesData<InfiniteData<ProjectAnnotationListResponse>>({
        queryKey,
        exact: false,
      });
      queryClient.setQueriesData<InfiniteData<ProjectAnnotationListResponse>>(
        { queryKey, exact: false },
        (current) =>
          current
            ? {
                ...current,
                pages: current.pages.map((page) =>
                  updateAnnotationInPage(page, annotationId, (annotation) =>
                    withRecomputedActionable(annotation, status, currentUserId)
                  )
                ),
              }
            : current
      );
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (!projectId || !screenplayId || !context?.previous) return;
      for (const [key, data] of context.previous) {
        queryClient.setQueryData(key, data);
      }
    },
    onSuccess: (updatedAnnotation) => {
      if (!projectId || !screenplayId) return;
      syncProjectAnnotationCachesAfterMutation(
        queryClient,
        projectId,
        screenplayId,
        updatedAnnotation
      );
    },
  });
}

export function useUpdateProjectAnnotationAcceptance(
  projectId: string | null,
  screenplayId: string | null
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      annotationId,
      payload,
    }: {
      annotationId: string;
      payload: ProjectAnnotationAcceptancePayload;
    }) => projectAnnotationApi.updateAcceptance(annotationId, payload),
    onSuccess: (updatedAnnotation) => {
      if (!projectId || !screenplayId) return;
      syncProjectAnnotationCachesAfterMutation(
        queryClient,
        projectId,
        screenplayId,
        updatedAnnotation
      );
    },
  });
}
